/**
 * What a visitor's device can do about putting the app on its home screen.
 *
 * Pure functions of the user agent and display mode, so every rule is testable
 * without a browser. `./hooks.ts` reads the real values and hands them here.
 *
 * The same rules as The Quiet Oracle's install page (its src/lib/install), so
 * both products behave alike on the same phone.
 */

export type Platform = 'ios' | 'android' | 'other'

export interface DeviceInput {
  userAgent: string
  /** `navigator.maxTouchPoints`. Tells an iPad (which reports a Mac) from a Mac. */
  maxTouchPoints?: number
}

export interface InstallEnvironment extends DeviceInput {
  /** Running as the installed app: `display-mode: standalone`, or iOS `navigator.standalone`. */
  isStandalone: boolean
  /** The browser has handed over a `beforeinstallprompt` event we can still call. */
  hasInstallPrompt: boolean
}

/**
 * What the install page shows.
 *
 * - `installed`: already running from the home screen.
 * - `prompt`: the browser has an install dialog we can open (Android, desktop Chrome/Edge).
 * - `ios-safari`: no install API; the two manual steps in Safari.
 * - `ios-other-browser`: an iPhone or iPad browser that cannot add to the home
 *   screen, or an in-app browser (Instagram, Gmail): open Safari first.
 * - `unsupported`: anything else; the app still works in the browser.
 */
export type InstallMode =
  | 'installed'
  | 'prompt'
  | 'ios-safari'
  | 'ios-other-browser'
  | 'unsupported'

/** iPadOS 13+ sends a Mac user agent; a Mac has no touch screen and an iPad does. */
function isIpadOnMacUserAgent({ userAgent, maxTouchPoints = 0 }: DeviceInput): boolean {
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1
}

export function detectPlatform(input: DeviceInput): Platform {
  if (/iPhone|iPad|iPod/.test(input.userAgent) || isIpadOnMacUserAgent(input)) return 'ios'
  if (/Android/.test(input.userAgent)) return 'android'
  return 'other'
}

/** A phone (not a tablet or a computer): the only place the home-screen card appears. */
export function isPhone(input: DeviceInput): boolean {
  if (/iPhone|iPod/.test(input.userAgent)) return true
  return /Android/.test(input.userAgent) && /Mobile/.test(input.userAgent)
}

/** Other browsers on iOS, which wrap the same engine but cannot add to the home screen. */
const IOS_OTHER_BROWSERS = /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|YaBrowser|Ecosia/
/** Apps that open links in their own web view. */
const IN_APP_BROWSERS =
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|GSA\/|Snapchat|MicroMessenger|TikTok|BytedanceWebview|musical_ly|Twitter|LinkedInApp|Pinterest|Gmail|Outlook|Slack|Telegram/

/**
 * Safari proper. A web view inside an app, and every third-party browser on
 * iOS, either lacks the `Safari/` token or carries the name of what it is.
 */
export function isIosSafari(input: DeviceInput): boolean {
  if (detectPlatform(input) !== 'ios') return false
  const ua = input.userAgent
  if (IOS_OTHER_BROWSERS.test(ua) || IN_APP_BROWSERS.test(ua)) return false
  return /Safari\//.test(ua)
}

export function installMode(env: InstallEnvironment): InstallMode {
  if (env.isStandalone) return 'installed'
  if (detectPlatform(env) === 'ios') return isIosSafari(env) ? 'ios-safari' : 'ios-other-browser'
  if (env.hasInstallPrompt) return 'prompt'
  return 'unsupported'
}

/**
 * The device a visitor said they want the app on, from the marketing site's
 * three buttons (`/install?for=…`). Each one names where the icon goes, so
 * the install page can say so when the visitor is holding something else.
 * `computer` covers Windows, a Mac and everything else that is not a phone.
 */
export type InstallTarget = 'android' | 'iphone' | 'computer'

const INSTALL_TARGETS: readonly InstallTarget[] = ['android', 'iphone', 'computer']

/** The `for` query value, or null when it is missing or not one of the three. */
export function parseInstallTarget(raw: string | null | undefined): InstallTarget | null {
  return INSTALL_TARGETS.find((t) => t === raw) ?? null
}

/** Which of the three buttons this device is. */
export function deviceTarget(input: DeviceInput): InstallTarget {
  const platform = detectPlatform(input)
  if (platform === 'ios') return 'iphone'
  if (platform === 'android') return 'android'
  return 'computer'
}

/**
 * The keys that bookmark a page. A computer gets a bookmark and a desktop
 * shortcut, never an install (Andy, 2026-11-02, as for The Quiet Oracle on
 * 2026-10-30). A web page cannot drop a working shortcut file on a Windows
 * desktop: Chrome and Edge rename a downloaded `.url` to `.download`.
 */
export function bookmarkKeys(input: DeviceInput): 'Cmd+D' | 'Ctrl+D' {
  return /Macintosh/.test(input.userAgent) && !isIpadOnMacUserAgent(input) ? 'Cmd+D' : 'Ctrl+D'
}

/** The two ways a browser says "this is the installed app". */
export function readStandalone(source: {
  matchesStandaloneDisplayMode: boolean
  navigatorStandalone: boolean | undefined
}): boolean {
  return source.matchesStandaloneDisplayMode || source.navigatorStandalone === true
}
